import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  ChevronDown, ChevronUp, Package, Tag, Users, Clock, Calendar,
  Image as ImageIcon, Settings, DollarSign, Box, FileText, Hash, 
  Video, LinkIcon, Megaphone, CheckCircle, Plus, X, Truck,
  Search, ExternalLink, AlertTriangle, Upload
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import TagsProductSelect from './TagsProductSelect';

/**
 * ProductForm - Formulaire produit unifié Admin/Partner
 * 
 * @param {Object} props
 * @param {string} props.role - 'admin' | 'partenaire'
 * @param {Object} props.formData - Données du formulaire
 * @param {Function} props.onFormChange - (field, value) => void
 * @param {Array} props.partners - Liste des partenaires (pour Admin)
 * @param {Object} props.editingProduct - Produit en cours d'édition (null si création)
 * @param {Function} props.onSubmit - Callback de soumission
 * @param {Function} props.onCancel - Callback d'annulation
 * @param {boolean} props.saving - État de sauvegarde
 * @param {Function} props.onAttachDistribution - Callback: rattacher distribution à produit existant (productId) => void
 */

// ═══════════════════════════════════════════════════════════
// COMPOSANT SECTION COLLAPSIBLE
// ═══════════════════════════════════════════════════════════
const Section = React.memo(({ id, title, icon: Icon, children, className = '', isExpanded, onToggle, badge, hasError = false, sectionRef }) => (
  <div 
    ref={sectionRef}
    className={`border rounded-lg overflow-hidden ${className} ${hasError ? 'border-red-500 border-2 ring-2 ring-red-100' : ''}`}
  >
    <button
      type="button"
      onClick={() => onToggle(id)}
      className={`w-full flex items-center justify-between p-3 transition-colors ${
        hasError ? 'bg-red-50 hover:bg-red-100' : 'bg-gray-50 hover:bg-gray-100'
      }`}
    >
      <div className="flex items-center gap-2 font-medium">
        <Icon className={`h-4 w-4 ${hasError ? 'text-red-500' : 'text-primary'}`} />
        <span className={hasError ? 'text-red-700' : ''}>{title}</span>
        {hasError && (
          <AlertTriangle className="h-4 w-4 text-red-500 animate-pulse" />
        )}
        {badge && !hasError && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
            {badge}
          </span>
        )}
      </div>
      {isExpanded ? (
        <ChevronUp className="h-4 w-4 text-muted-foreground" />
      ) : (
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      )}
    </button>
    {isExpanded && (
      <div className="p-4 space-y-4">
        {children}
      </div>
    )}
  </div>
));

Section.displayName = 'Section';

// ═══════════════════════════════════════════════════════════
// GESTION DES IMAGES (STANDARD UNIQUE)
// ═══════════════════════════════════════════════════════════
const ImageManager = React.memo(({ visuels = [], onVisuelsChange, maxImages = 5 }) => {
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const images = useMemo(() => {
    if (Array.isArray(visuels)) return visuels;
    if (typeof visuels === 'string' && visuels.trim()) {
      return visuels.split(',').map(v => v.trim()).filter(v => v);
    }
    return [];
  }, [visuels]);

  const addImage = useCallback(() => {
    if (!newImageUrl.trim()) return;
    if (!newImageUrl.startsWith('http')) {
      toast.error('L\'URL doit commencer par http:// ou https://');
      return;
    }
    if (images.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images autorisées`);
      return;
    }
    const updatedImages = [...images, newImageUrl.trim()];
    onVisuelsChange(updatedImages);
    setNewImageUrl('');
  }, [newImageUrl, images, maxImages, onVisuelsChange]);

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    if (images.length >= maxImages) {
      toast.error(`Maximum ${maxImages} images autorisées`);
      return;
    }
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await api.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = response.data.url;
      onVisuelsChange([...images, imageUrl]);
      toast.success('Image uploadée avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  }, [images, maxImages, onVisuelsChange]);

  const removeImage = useCallback((index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    onVisuelsChange(updatedImages);
  }, [images, onVisuelsChange]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Label className="flex items-center gap-2">
          <ImageIcon className="h-4 w-4" />
          Images produit ({images.length}/{maxImages})
        </Label>
      </div>

      {/* Grid des images existantes */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {images.map((url, idx) => (
            <div key={idx} className="relative group aspect-square">
              <img
                src={url}
                alt={`Visuel ${idx + 1}`}
                className="w-full h-full object-cover rounded-lg border bg-gray-50"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/150?text=Erreur';
                }}
              />
              <button
                type="button"
                onClick={() => removeImage(idx)}
                className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
              >
                <X className="h-3 w-3" />
              </button>
              <span className="absolute bottom-1 left-1 text-xs bg-black/60 text-white px-1.5 py-0.5 rounded">
                {idx + 1}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Ajout d'une nouvelle image — URL ou Upload */}
      {images.length < maxImages && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              value={newImageUrl}
              onChange={(e) => setNewImageUrl(e.target.value)}
              placeholder="https://exemple.com/image.jpg"
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addImage())}
              data-testid="input-visuel-url"
            />
            <Button type="button" variant="outline" onClick={addImage} data-testid="btn-add-url">
              <Plus className="h-4 w-4 mr-1" />
              URL
            </Button>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => { handleFileUpload(e.target.files?.[0]); e.target.value = ''; }}
                data-testid="input-upload-visuel"
              />
              <Button type="button" variant="outline" disabled={uploading} asChild>
                <span>
                  <Upload className={`h-4 w-4 mr-1 ${uploading ? 'animate-pulse' : ''}`} />
                  {uploading ? '...' : 'Fichier'}
                </span>
              </Button>
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            Collez une URL ou uploadez un fichier image (PNG, JPG, WebP, GIF). Maximum {maxImages} images.
          </p>
        </div>
      )}
    </div>
  );
});

ImageManager.displayName = 'ImageManager';

// ═══════════════════════════════════════════════════════════
// CONSTANTES - Listes de valeurs (fallback si API indisponible)
// Phase 2.1: Les valeurs sont chargées depuis /api/referentiels
// ═══════════════════════════════════════════════════════════
const FALLBACK_TYPES_JEU = [
  'Stratégie', 'Ambiance', 'Familial', 'Expert', 'Coopératif', 
  'Abstrait', 'Deckbuilding', 'Gestion', 'Narratif', 'Party Game',
  'TCG', 'Jeu de rôle', 'Puzzle', 'Enfant', 'Adresse', 'Aventure', 'Quiz'
];

const FALLBACK_PUBLICS_CIBLES = [
  'Enfant', 'Famille', 'Casual', 'Confirmé', 'Expert', 'Tous publics'
];

// Phase 3.4.6: FALLBACK_MECANISMES supprimé (remplacé par tags_produit catégorie mecanique)

const FALLBACK_LANGUES = ['FR', 'EN', 'DE', 'ES', 'IT', 'NL', 'PL', 'SANS_LANGUE'];

const LANGUE_LABELS = {
  'FR': 'Français', 'EN': 'Anglais', 'DE': 'Allemand', 'ES': 'Espagnol',
  'IT': 'Italien', 'NL': 'Néerlandais', 'PL': 'Polonais', 'SANS_LANGUE': 'Sans langue',
};

// ═══════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════
const ProductForm = ({
  role,
  formData,
  onFormChange,
  partners = [],
  editingProduct = null,
  onSubmit,
  onCancel,
  saving = false,
  onAttachDistribution = null,
  editorsSlot = null,
  distributionsSlot = null
}) => {
  const isAdmin = role === 'admin';
  const isPartner = role === 'partenaire';

  // Phase 2.1: Load referentiels from API (administrable lists)
  const [referentiels, setReferentiels] = useState({
    types_jeu: FALLBACK_TYPES_JEU,
    publics_cibles: FALLBACK_PUBLICS_CIBLES,
    langues: FALLBACK_LANGUES,
  });

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const types = ['types_jeu', 'publics_cibles', 'langues'];
        const results = await Promise.all(
          types.map(t => api.get(`/referentiels/${t}`).catch(() => null))
        );
        const newRefs = {};
        types.forEach((t, i) => {
          if (results[i]?.data?.items?.length > 0) {
            newRefs[t] = results[i].data.items.map(item => item.value);
          }
        });
        if (Object.keys(newRefs).length > 0) {
          setReferentiels(prev => ({ ...prev, ...newRefs }));
        }
      } catch { /* fallback to hardcoded */ }
    };
    loadRefs();
  }, []);

  // === EAN LOOKUP STATE ===
  const [eanLookup, setEanLookup] = useState({ loading: false, result: null });
  const eanTimerRef = useRef(null);
  const isCreating = !editingProduct;

  // NOTE: lookupEan et handleEanChange sont déclarés APRÈS handleChange (voir plus bas)

  // Cleanup timer
  useEffect(() => {
    return () => { if (eanTimerRef.current) clearTimeout(eanTimerRef.current); };
  }, []);

  // Sections collapsibles
  const [expandedSections, setExpandedSections] = useState({
    identification: true,
    presentation: true,
    caracteristiques: true,
    pricing: true,
    logistique: false,
    disponibilite: false,
    marketing: false,
    systeme: false
  });

  // ═══════════════════════════════════════════════════════════
  // VALIDATION MÉTIER - Promotion migrée vers distributions (V3)
  // ═══════════════════════════════════════════════════════════

  // Handler de soumission
  const handleFormSubmit = useCallback((e) => {
    e.preventDefault();
    onSubmit(e);
  }, [onSubmit]);

  const handleToggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, []);

  const handleChange = useCallback((field, value) => {
    onFormChange(field, value);
  }, [onFormChange]);

  // Gestion des images avec conversion array <-> string
  const handleVisuelsChange = useCallback((imagesArray) => {
    // Stocker en array directement
    onFormChange('visuels', imagesArray);
  }, [onFormChange]);

  // Conversion des visuels pour ImageManager
  const visuelsArray = useMemo(() => {
    if (Array.isArray(formData.visuels)) return formData.visuels;
    if (typeof formData.visuels === 'string' && formData.visuels.trim()) {
      return formData.visuels.split(',').map(v => v.trim()).filter(v => v);
    }
    return [];
  }, [formData.visuels]);

  // === EAN LOOKUP LOGIC (après handleChange pour éviter TDZ) ===
  const lookupEan = useCallback(async (ean) => {
    if (!ean || ean.trim().length < 8 || !isCreating) {
      setEanLookup({ loading: false, result: null });
      return;
    }
    setEanLookup(prev => ({ ...prev, loading: true }));
    try {
      const response = await api.get(`/products/lookup-ean/${encodeURIComponent(ean.trim())}`);
      setEanLookup({ loading: false, result: response.data });
    } catch {
      setEanLookup({ loading: false, result: null });
    }
  }, [isCreating]);

  const handleEanChange = useCallback((value) => {
    handleChange('code_ean', value);
    if (eanTimerRef.current) clearTimeout(eanTimerRef.current);
    if (isCreating && value && value.trim().length >= 8) {
      eanTimerRef.current = setTimeout(() => lookupEan(value), 500);
    } else {
      setEanLookup({ loading: false, result: null });
    }
  }, [handleChange, isCreating, lookupEan]);

  return (
    <form onSubmit={handleFormSubmit} className="space-y-4">
      
      {/* ═══════════════════════════════════════════════════════════
          SECTION 1 - IDENTIFICATION PRODUIT
      ═══════════════════════════════════════════════════════════ */}
      <Section 
        id="identification" 
        title="Identification Produit" 
        icon={Tag} 
        isExpanded={expandedSections.identification} 
        onToggle={handleToggleSection}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="intitule">{"Désignation produit"} <span className="text-red-500">*</span></Label>
            <Input
              id="intitule"
              value={formData.intitule || ''}
              onChange={(e) => handleChange('intitule', e.target.value)}
              placeholder="Ex: Azul"
              required
              data-testid="input-intitule"
            />
          </div>
          
          <div>
            <Label htmlFor="code_ean">Code EAN <span className="text-red-500">*</span></Label>
            <div className="relative">
              <Input
                id="code_ean"
                value={formData.code_ean || ''}
                onChange={(e) => handleEanChange(e.target.value)}
                placeholder="3760093330015"
                data-testid="input-code_ean"
                className={eanLookup.result?.found ? 'border-amber-400 pr-8' : ''}
              />
              {eanLookup.loading && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Search className="h-4 w-4 text-gray-400 animate-pulse" />
                </div>
              )}
            </div>
            
            {/* === EAN LOOKUP RESULT — Produit existant trouvé === */}
            {isCreating && eanLookup.result?.found && (
              <div className="mt-2 p-3 rounded-lg border border-amber-300 bg-amber-50" data-testid="ean-lookup-found">
                <div className="flex items-start gap-3">
                  {eanLookup.result.product.image ? (
                    <img src={eanLookup.result.product.image} alt="" className="w-14 h-14 object-cover rounded" />
                  ) : (
                    <div className="w-14 h-14 bg-gray-200 rounded flex items-center justify-center">
                      <Package className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-900">Produit existant trouvé</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{eanLookup.result.product.intitule}</p>
                    <p className="text-xs text-gray-600">
                      EAN: {eanLookup.result.product.code_ean}
                      {eanLookup.result.product.editeur && ` — ${eanLookup.result.product.editeur}`}
                      {!eanLookup.result.product.editeur && eanLookup.result.product.original_publishers?.[0]?.name && ` — ${eanLookup.result.product.original_publishers[0].name}`}
                    </p>
                    <p className="text-xs text-gray-500">{eanLookup.result.product.distributions_count} distributeur(s)</p>
                  </div>
                </div>
                {onAttachDistribution && (
                  <div className="mt-2 flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={() => onAttachDistribution(eanLookup.result.product.id, eanLookup.result.product)}
                      data-testid="btn-attach-distribution"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Ajouter ma distribution
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEanLookup({ loading: false, result: null })}
                    >
                      Ignorer
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* ref_distributeur: DEPRECATED — migré vers distributions[].supplier_reference. Géré via AdminDistributionsManager */}
          
          {/* TAGS - Admin uniquement */}
          {isAdmin && (
            <div className="md:col-span-2">
              <Label htmlFor="tags">Tags (séparés par virgule)</Label>
              <Input
                id="tags"
                value={typeof formData.tags === 'string' ? formData.tags : (formData.tags?.join(', ') || '')}
                onChange={(e) => handleChange('tags', e.target.value)}
                placeholder="bestseller, incontournable, exclusif"
                data-testid="input-tags"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tags manuels: bestseller, incontournable, coup_de_coeur, exclusif, limité, top_vente
                <br />
                <span className="text-amber-600">Note: Les tags precommande, nouveaute, promo, selection sont auto-générés.</span>
              </p>
            </div>
          )}
        </div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 2 - DATES & CYCLE DE VIE
      ═══════════════════════════════════════════════════════════ */}
      <Section 
        id="disponibilite" 
        title="Dates & Cycle de vie" 
        icon={Calendar} 
        isExpanded={expandedSections.disponibilite} 
        onToggle={handleToggleSection}
      >
        <div className="mb-4">
          <Label htmlFor="date_sortie_iso">Date de sortie</Label>
          <Input
            id="date_sortie_iso"
            type="date"
            value={formData.date_sortie_iso || ''}
            onChange={(e) => handleChange('date_sortie_iso', e.target.value)}
            data-testid="input-date_sortie"
            className="max-w-xs"
          />
        </div>
        {isAdmin && (
          <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-sm font-medium text-purple-700 mb-3">Sélection du mois (Admin)</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date_debut_selection_iso">Début</Label>
                <Input
                  id="date_debut_selection_iso"
                  type="date"
                  value={formData.date_debut_selection_iso || ''}
                  onChange={(e) => handleChange('date_debut_selection_iso', e.target.value)}
                  data-testid="input-date_debut_selection"
                />
              </div>
              <div>
                <Label htmlFor="date_fin_selection_iso">Fin</Label>
                <Input
                  id="date_fin_selection_iso"
                  type="date"
                  value={formData.date_fin_selection_iso || ''}
                  onChange={(e) => handleChange('date_fin_selection_iso', e.target.value)}
                  data-testid="input-date_fin_selection"
                />
              </div>
            </div>
          </div>
        )}
      </Section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 3 - PRICING
      ═══════════════════════════════════════════════════════════ */}
      <Section 
        id="pricing" 
        title="Pricing" 
        icon={DollarSign} 
        className="border-amber-200 bg-amber-50/30" 
        isExpanded={expandedSections.pricing} 
        onToggle={handleToggleSection}
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="prix_vente_conseille">PVC TTC</Label>
            <Input
              id="prix_vente_conseille"
              type="number"
              step="0.01"
              min="0"
              value={formData.prix_vente_conseille ?? ''}
              onChange={(e) => handleChange('prix_vente_conseille', e.target.value)}
              placeholder="39.90"
              data-testid="input-prix_vente_conseille"
            />
          </div>
          <div>
            <Label htmlFor="tva">TVA (%)</Label>
            <Input
              id="tva"
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={formData.tva ?? ''}
              onChange={(e) => handleChange('tva', e.target.value)}
              placeholder="20"
              data-testid="input-tva"
            />
          </div>
        </div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 4 - PRÉSENTATION PRODUIT
      ═══════════════════════════════════════════════════════════ */}
      <Section 
        id="presentation" 
        title="Présentation Produit" 
        icon={FileText} 
        isExpanded={expandedSections.presentation} 
        onToggle={handleToggleSection}
      >
        {/* Éditeurs — slot ProductPublisher (injecté via prop) */}
        {editorsSlot && (
          <div className="mb-4">
            {editorsSlot}
          </div>
        )}
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="auteur">Auteur(s)</Label>
            <Input
              id="auteur"
              value={formData.auteur || ''}
              onChange={(e) => handleChange('auteur', e.target.value)}
              placeholder="Michael Kiesling"
              data-testid="input-auteur"
            />
          </div>
          
          <div className="sm:col-span-2">
            <Label htmlFor="illustrateur">Illustrateur(s)</Label>
            <Input
              id="illustrateur"
              value={formData.illustrateur || ''}
              onChange={(e) => handleChange('illustrateur', e.target.value)}
              placeholder="Chris Quilliams"
              data-testid="input-illustrateur"
            />
          </div>
        </div>
        
        <div className="mt-4 space-y-4">
          <div>
            <Label htmlFor="description_courte">Description courte</Label>
            <Input
              id="description_courte"
              value={formData.description_courte || ''}
              onChange={(e) => handleChange('description_courte', e.target.value.slice(0, 200))}
              placeholder="Résumé en une phrase pour les listes produits"
              maxLength={200}
              data-testid="input-description_courte"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {(formData.description_courte || '').length}/200 caractères
            </p>
          </div>
          
          <div>
            <Label htmlFor="descriptif">Description complète <span className="text-red-500">*</span></Label>
            <Textarea
              id="descriptif"
              value={formData.descriptif || ''}
              onChange={(e) => handleChange('descriptif', e.target.value)}
              rows={4}
              placeholder="Description détaillée du produit..."
              data-testid="input-descriptif"
            />
          </div>
          
          <div>
            <Label htmlFor="liste_materiel">Contenu de la boîte</Label>
            <Textarea
              id="liste_materiel"
              value={formData.liste_materiel || ''}
              onChange={(e) => handleChange('liste_materiel', e.target.value)}
              rows={3}
              placeholder="1 plateau de jeu, 100 tuiles, 4 pions..."
              data-testid="input-liste_materiel"
            />
          </div>
        </div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 3 - CARACTÉRISTIQUES PRODUITS
      ═══════════════════════════════════════════════════════════ */}
      <Section 
        id="caracteristiques" 
        title="Caractéristiques Produits" 
        icon={Users} 
        isExpanded={expandedSections.caracteristiques} 
        onToggle={handleToggleSection}
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="nb_joueurs_min">Joueurs min</Label>
            <Input
              id="nb_joueurs_min"
              type="number"
              min="1"
              value={formData.nb_joueurs_min ?? ''}
              onChange={(e) => handleChange('nb_joueurs_min', e.target.value)}
              placeholder="2"
              data-testid="input-nb_joueurs_min"
            />
          </div>
          
          <div>
            <Label htmlFor="nb_joueurs_max">Joueurs max</Label>
            <Input
              id="nb_joueurs_max"
              type="number"
              min="1"
              value={formData.nb_joueurs_max ?? ''}
              onChange={(e) => handleChange('nb_joueurs_max', e.target.value)}
              placeholder="4"
              data-testid="input-nb_joueurs_max"
            />
          </div>
          
          <div>
            <Label htmlFor="age_minimum">Âge minimum</Label>
            <Input
              id="age_minimum"
              type="number"
              min="0"
              value={formData.age_minimum ?? ''}
              onChange={(e) => handleChange('age_minimum', e.target.value)}
              placeholder="8"
              data-testid="input-age_minimum"
            />
          </div>
          
          <div>
            <Label htmlFor="temps_moyen_partie">Durée (minutes)</Label>
            <Input
              id="temps_moyen_partie"
              type="number"
              min="0"
              value={formData.temps_moyen_partie ?? ''}
              onChange={(e) => handleChange('temps_moyen_partie', e.target.value)}
              placeholder="45"
              data-testid="input-temps_moyen_partie"
            />
          </div>
          
          <div>
            <Label>Type de jeu</Label>
            <Select
              value={formData.type_jeu || ''}
              onValueChange={(v) => handleChange('type_jeu', v)}
            >
              <SelectTrigger data-testid="select-type_jeu">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {referentiels.types_jeu.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Public cible</Label>
            <Select
              value={formData.public_cible || ''}
              onValueChange={(v) => handleChange('public_cible', v)}
            >
              <SelectTrigger data-testid="select-public_cible">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {referentiels.publics_cibles.map(pub => (
                  <SelectItem key={pub} value={pub}>{pub}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Tags Produit</Label>
            <TagsProductSelect
              selectedIds={formData.tags_produit_ids || []}
              onChange={(ids) => handleChange('tags_produit_ids', ids)}
              disabled={false}
            />
          </div>
          
          <div>
            <Label>Langue(s)</Label>
            <div className="flex flex-wrap gap-1.5 p-2 border rounded-md min-h-[40px]" data-testid="select-langue">
              {referentiels.langues.map(code => {
                const currentLangs = Array.isArray(formData.langue) ? formData.langue : (formData.langue ? [formData.langue] : []);
                const isSelected = currentLangs.includes(code);
                const isSansLangue = code === 'SANS_LANGUE';
                const label = LANGUE_LABELS[code] || code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => {
                      let next;
                      if (isSansLangue) {
                        next = isSelected ? [] : ['SANS_LANGUE'];
                      } else {
                        const filtered = currentLangs.filter(c => c !== 'SANS_LANGUE');
                        next = isSelected ? filtered.filter(c => c !== code) : [...filtered, code];
                      }
                      handleChange('langue', next.length > 0 ? next : null);
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
                      isSelected
                        ? (isSansLangue ? 'bg-gray-700 text-white border-gray-700' : 'bg-blue-600 text-white border-blue-600')
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`langue-${code}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {Array.isArray(formData.langue) && formData.langue.length === 0 && (
              <p className="text-[10px] text-muted-foreground mt-1">Aucune langue sélectionnée (non renseigné)</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="pays_provenance">Pays d&apos;origine</Label>
            <Input
              id="pays_provenance"
              value={formData.pays_provenance || ''}
              onChange={(e) => handleChange('pays_provenance', e.target.value)}
              placeholder="Allemagne, France..."
              data-testid="input-pays_provenance"
            />
          </div>
          
          <div>
            <Label htmlFor="taille_boite">Dimensions boîte</Label>
            <Input
              id="taille_boite"
              value={formData.taille_boite || ''}
              onChange={(e) => handleChange('taille_boite', e.target.value)}
              placeholder="30x30x7 cm"
              data-testid="input-taille_boite"
            />
          </div>
          
          <div>
            <Label htmlFor="poids_boite">Poids boîte (kg)</Label>
            <Input
              id="poids_boite"
              type="number"
              step="0.01"
              min="0"
              value={formData.poids_boite ?? ''}
              onChange={(e) => handleChange('poids_boite', e.target.value)}
              placeholder="1.2"
              data-testid="input-poids_boite"
            />
          </div>
        </div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 6 - MÉDIAS & MARKETING
      ═══════════════════════════════════════════════════════════ */}
      <Section 
        id="marketing" 
        title="Médias & Marketing" 
        icon={Megaphone} 
        isExpanded={expandedSections.marketing} 
        onToggle={handleToggleSection}
      >
        {/* IMAGES - Standard unique avec preview/suppression */}
        <ImageManager
          visuels={visuelsArray}
          onVisuelsChange={handleVisuelsChange}
          maxImages={5}
        />
        
        {/* Atouts marketing */}
        <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-200">
          <p className="text-sm font-semibold text-orange-800 mb-3">Points forts (Atouts marketing)</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="atouts_1">Atout 1</Label>
              <Input
                id="atouts_1"
                value={formData.atouts_1 || ''}
                onChange={(e) => handleChange('atouts_1', e.target.value)}
                placeholder="Jeu familial accessible"
                data-testid="input-atouts_1"
              />
            </div>
            <div>
              <Label htmlFor="atouts_2">Atout 2</Label>
              <Input
                id="atouts_2"
                value={formData.atouts_2 || ''}
                onChange={(e) => handleChange('atouts_2', e.target.value)}
                placeholder="Règles simples"
                data-testid="input-atouts_2"
              />
            </div>
            <div>
              <Label htmlFor="atouts_3">Atout 3</Label>
              <Input
                id="atouts_3"
                value={formData.atouts_3 || ''}
                onChange={(e) => handleChange('atouts_3', e.target.value)}
                placeholder="Design moderne"
                data-testid="input-atouts_3"
              />
            </div>
          </div>
        </div>
        
        {/* Pourquoi référencer ce jeu */}
        <div className="mt-4">
          <Label htmlFor="pourquoi_referencer" className="flex items-center gap-2">
            <Megaphone className="h-4 w-4" />
            Pourquoi référencer ce jeu ?
          </Label>
          <Textarea
            id="pourquoi_referencer"
            value={formData.pourquoi_referencer || ''}
            onChange={(e) => handleChange('pourquoi_referencer', e.target.value)}
            rows={3}
            placeholder="Expliquez pourquoi un retailer devrait référencer ce produit (angle commercial B2B)..."
            data-testid="input-pourquoi_referencer"
          />
        </div>
        
        {/* Liens médias */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label htmlFor="video_url" className="flex items-center gap-2">
              <Video className="h-4 w-4" />
              URL Vidéo
            </Label>
            <Input
              id="video_url"
              value={formData.video_url || ''}
              onChange={(e) => handleChange('video_url', e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              data-testid="input-video_url"
            />
          </div>
          
          <div>
            <Label htmlFor="regle_jeu_url" className="flex items-center gap-2">
              <LinkIcon className="h-4 w-4" />
              URL Règles du jeu (PDF)
            </Label>
            <Input
              id="regle_jeu_url"
              value={formData.regle_jeu_url || ''}
              onChange={(e) => handleChange('regle_jeu_url', e.target.value)}
              placeholder="https://editeur.com/regles.pdf"
              data-testid="input-regle_jeu_url"
            />
          </div>
        </div>
        
        {/* Plan communication */}
        <div className="mt-4">
          <Label htmlFor="plan_communication_commercial">Plan de communication commerciale</Label>
          <Textarea
            id="plan_communication_commercial"
            value={formData.plan_communication_commercial || ''}
            onChange={(e) => handleChange('plan_communication_commercial', e.target.value)}
            rows={3}
            placeholder="Campagne social media prévue en mars, PLV disponible..."
            data-testid="input-plan_communication_commercial"
          />
        </div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 8 - SYSTÈME
      ═══════════════════════════════════════════════════════════ */}
      <Section 
        id="systeme" 
        title="Système" 
        icon={Settings} 
        isExpanded={expandedSections.systeme} 
        onToggle={handleToggleSection}
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <Label>Statut produit</Label>
            <div className="flex items-center gap-3 mt-2">
              <Switch
                id="is_active"
                checked={formData.is_active ?? true}
                onCheckedChange={(checked) => handleChange('is_active', checked)}
                data-testid="switch-is_active"
              />
              <Label htmlFor="is_active" className="font-normal">
                {formData.is_active ? 'Actif' : 'Inactif'}
              </Label>
            </div>
          </div>
          
          {/* ============================================================================
              CONTRÔLE ADMIN - download_fiche_enabled
              Toggle visible uniquement pour Admin
              Permet d'activer/désactiver le téléchargement de la fiche produit
          ============================================================================ */}
          {isAdmin && (
            <div>
              <Label>Téléchargement fiche</Label>
              <div className="flex items-center gap-3 mt-2">
                <Switch
                  id="download_fiche_enabled"
                  checked={formData.download_fiche_enabled ?? false}
                  onCheckedChange={(checked) => handleChange('download_fiche_enabled', checked)}
                  data-testid="switch-download_fiche"
                />
                <Label htmlFor="download_fiche_enabled" className="font-normal text-sm">
                  {formData.download_fiche_enabled ? 'Activé' : 'Désactivé'}
                </Label>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Affiche le bouton de téléchargement dans la fiche produit
              </p>
            </div>
          )}
          
          {/* Infos lecture seule - Admin uniquement */}
          {isAdmin && editingProduct && (
            <>
              <div>
                <Label>ID</Label>
                <p className="text-sm font-mono text-muted-foreground bg-gray-100 p-2 rounded mt-1 truncate" title={editingProduct.id}>
                  {editingProduct.id}
                </p>
              </div>
              
              <div>
                <Label>Créé le</Label>
                <p className="text-sm text-muted-foreground bg-gray-100 p-2 rounded mt-1">
                  {editingProduct.created_at ? new Date(editingProduct.created_at).toLocaleDateString('fr-FR') : '-'}
                </p>
              </div>
              
              <div>
                <Label>Modifié le</Label>
                <p className="text-sm text-muted-foreground bg-gray-100 p-2 rounded mt-1">
                  {editingProduct.updated_at ? new Date(editingProduct.updated_at).toLocaleDateString('fr-FR') : '-'}
                </p>
              </div>
              
              <div>
                <Label>Ajouts à la Wishlist</Label>
                <p className="text-sm text-muted-foreground bg-gray-100 p-2 rounded mt-1">
                  {editingProduct.nb_favoris || 0}
                </p>
              </div>
              
              <div>
                <Label>À suivre</Label>
                <p className="text-sm text-muted-foreground bg-gray-100 p-2 rounded mt-1">
                  {editingProduct.nb_a_suivre || 0}
                </p>
              </div>
            </>
          )}
        </div>
      </Section>

      {/* ═══════════════════════════════════════════════════════════
          SECTION DISTRIBUTEURS (slot)
      ═══════════════════════════════════════════════════════════ */}
      {distributionsSlot && (
        <div className="mt-2">
          {distributionsSlot}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 justify-end pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Annuler
        </Button>
        <Button type="submit" disabled={saving} data-testid="submit-product-btn">
          {saving ? 'Enregistrement...' : (editingProduct ? 'Enregistrer les modifications' : 'Créer le produit')}
        </Button>
      </div>
    </form>
  );
};

export default ProductForm;
